import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { DatePicker } from '../../components/DatePicker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Sparkles, Loader2 } from 'lucide-react';
import { useBrands } from '../../utils/brands';

const TASK_CATEGORIES = ['OnPage', 'OffPage', 'Technical', 'Others'];

const ConvertToTaskModal = ({ open, onClose, note, users, onConfirm }) => {
  const TASK_BRANDS = useBrands();
  const [category, setCategory] = useState('Others');
  const [brand, setBrand] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm({
        category,
        brand: brand || null,
        assigned_to: assignedTo || null,
        due_date: dueDate ? new Date(dueDate).toISOString() : null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="convert-task-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="text-amber-500" size={18} /> Convert Note to Task
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm">
            <p className="font-semibold text-amber-900">{note?.title || 'Untitled'}</p>
            {note?.content && (
              <p className="text-amber-800 mt-1 line-clamp-2 text-xs">{note.content}</p>
            )}
            <p className="text-[11px] text-amber-700 mt-2">
              The note will be archived and linked to the new task.
            </p>
          </div>

          <div>
            <Label>Task Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="convert-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TASK_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Brand</Label>
            <Select value={brand || 'none'} onValueChange={(v) => setBrand(v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="convert-brand-select">
                <SelectValue placeholder="No brand" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No brand</SelectItem>
                {TASK_BRANDS.map((b) => (
                  <SelectItem key={b} value={b}>
                    {b}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Assignee</Label>
            <Select
              value={assignedTo || 'none'}
              onValueChange={(v) => setAssignedTo(v === 'none' ? '' : v)}
            >
              <SelectTrigger data-testid="convert-assignee-select">
                <SelectValue placeholder="Assign to..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Assign to me</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.full_name || u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="convert-due-date">Due Date (optional)</Label>
            <DatePicker
              value={dueDate}
              onChange={setDueDate}
              testId="convert-due-date"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting}
            className="bg-gradient-to-r from-amber-500 to-orange-500"
            data-testid="convert-confirm-btn"
          >
            {submitting ? (
              <><Loader2 className="animate-spin mr-1" size={14} /> Converting...</>
            ) : (
              'Create Task'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConvertToTaskModal;
